// スカウト一覧画面でのスカウトテンプレートで使用するjs
import { Controller } from "@hotwired/stimulus";
import { getCsrfToken } from "./lib/utils";

function fetchStores(brandId) {
    const ENDPOINT_URL = `/api/v1/stores/search.json?brand_id=${brandId}&offered=true`;
    return fetch(ENDPOINT_URL)
        .then(res => res.json())
        .then(json => {
            if (json.status == "success") {
                return json.data;
            }
        })
        .catch(error => console.log(error));
}

export default class extends Controller {
    static targets = [
        "templateid",
        "templateTitle",
        "scouttype",
        "brandid",
        "storebrandid",
        "storeid",
        "storeList",
        "sender",
        "title",
        "content",
        "submit",
    ];

    static values = {
        formId: String,
        templateid: String,
        templateTitle: String,
        scouttype: String,
        companyid: String,
        brandid: String,
        storebrandid: String,
        storeid: String,
        sender: String,
        title: String,
        content: String,
        offerid: String,
    };

    connect() {
        this._setValueAllTargets();
        this._validate();
    }

    // titleに入力された時
    templateTitleValueHandler(event) {
        this.templateTitleValue = event.target.value;
        this._validate();
    }

    // scouttypeがform上で選択された時
    scouttypeValueHandler(event) {
        let selectValue = event.target.value;
        this.scouttypeValue = selectValue;
        this._displayOnScouttype(selectValue);
        this._validate();
    }

    // scouttypeがvalueによって変更された時
    scouttypeValueChanged(value) {
        this._displayOnScouttype(value);
    }

    // scouttypeがbrand時にbrandidが変更された時
    brandidValueForBrandHandler(event) {
        let selectValue = event.target.value;

        if (this.scouttypeValue === "brand") {
            this.brandidValue = selectValue;
        }
        this._validate();
    }

    // scouttypeがstore時にbrandidが変更された時
    brandidValueForStoreHandler(event) {
        let selectValue = event.target.value;
        if (this.scouttypeValue === "store") {
            if (this.storebrandidValue !== selectValue) {
                this._renderBrandStoreSelectOptions(
                    selectValue,
                    this.storeidValue
                );
                this.storebrandidValue = selectValue;
            }
        }
        this._validate();
    }

    // scouttypeがstore時にstoreidが変更された時
    storeidValueForStoreHandler(event) {
        let selectValue = event.target.value;

        if (this.scouttypeValue === "store") {
            this.storeidValue = selectValue;
            this.storeidTarget.value = selectValue;
        }
        this._validate();
    }

    // senderが変更された時
    senderValueHandler(event) {
        this.senderValue = event.target.value;
        this._validate();
    }

    // titleが変更された時
    titleValueHandler(event) {
        this.titleValue = event.target.value;
        this._validate();
    }

    // contentが変更された時
    contentValueHandler(event) {
        this.contentValue = event.target.value;
        this._validate();
    }

    // 添付求人が変更された時（求人選択モーダルから呼び出される）
    offeridValueHandler(value, _label, cardHtml) {
        this.offeridValue = value || "";
        // 求人タイトルは選択済みカードに表示する仕様のため、「選択」ボタンのラベルは変更しない
        if (cardHtml) {
            this._renderSelectedOfferCard(cardHtml);
        } else if (this.offeridValue) {
            this._fetchSelectedOfferCard(this.offeridValue);
        } else {
            this._hideSelectedOfferCard();
        }
    }

    _renderSelectedOfferCard(cardHtml) {
        const selectedCard = this.element.querySelector('[data-js-selected-offer-card]');
        if (!selectedCard) return;

        const cardInner = selectedCard.querySelector('.c-block-54');
        if (cardInner) {
            cardInner.innerHTML = cardHtml;
            // 募集中以外は data-offer-active="false" がpartial側で埋め込まれる。古いキャッシュなどで属性が無い場合は安全側にtrue扱い
            const marker = cardInner.querySelector('[data-offer-active]');
            const active = !marker || marker.dataset.offerActive === 'true';
            cardInner.classList.toggle('is-disabled', !active);

            const errorMsg = this.element.querySelector('[data-js-selected-offer-error]');
            if (errorMsg) errorMsg.classList.toggle('is-hide', active);
        }
        selectedCard.classList.remove('is-hide');
        // 添付求人カードは非同期で再描画されるため、レンダリング完了後に保存ボタンの状態を再評価する
        this._validate();
    }

    _isAttachedOfferActive() {
        const cardInner = this.element.querySelector('[data-js-selected-offer-card] .c-block-54');
        if (!cardInner) return true;
        const marker = cardInner.querySelector('[data-offer-active]');
        return !marker || marker.dataset.offerActive === 'true';
    }

    // 添付求人とスカウト単位（ブランド/店舗）のスコープが一致しているか判定する
    _isAttachedOfferScopeMatched() {
        if (!this.offeridValue) return true;
        if (this.scouttypeValue === 'company') return true;

        if (this.scouttypeValue === 'brand') {
            const attachedOfferBrandId = this._getAttachedOfferBrandId();
            if (!this.brandidValue || !attachedOfferBrandId) return true;
            return String(attachedOfferBrandId) === String(this.brandidValue);
        }
        if (this.scouttypeValue === 'store') {
            const attachedOfferStoreId = this._getAttachedOfferStoreId();
            if (!this.storeidValue || !attachedOfferStoreId) return true;
            return String(attachedOfferStoreId) === String(this.storeidValue);
        }
        return true;
    }

    _getAttachedOfferBrandId() {
        const marker = this.element.querySelector('[data-js-selected-offer-card] [data-offer-brand-id]');
        return marker ? marker.dataset.offerBrandId : null;
    }

    _getAttachedOfferStoreId() {
        const marker = this.element.querySelector('[data-js-selected-offer-card] [data-offer-store-id]');
        return marker ? marker.dataset.offerStoreId : null;
    }

    _renderAttachedOfferScopeMismatchError() {
        const errorEl = this.element.querySelector('[data-js-selected-offer-scope-error]');
        if (!errorEl) return;

        if (!this.offeridValue || this._isAttachedOfferScopeMatched()) {
            errorEl.classList.add('is-hide');
            errorEl.textContent = '';
            return;
        }

        if (this.scouttypeValue === 'brand') {
            errorEl.textContent = '添付求人のブランドが、スカウト単位のブランドと異なります。指定しているブランドの求人を添付してください。';
        } else if (this.scouttypeValue === 'store') {
            errorEl.textContent = '添付求人のブランドまたは店舗が、スカウト単位のブランドまたは店舗と異なります。指定しているブランドまたは店舗の求人を添付してください。';
        } else {
            errorEl.textContent = '';
        }
        errorEl.classList.remove('is-hide');
    }

    _hideSelectedOfferCard() {
        const selectedCard = this.element.querySelector('[data-js-selected-offer-card]');
        if (!selectedCard) return;

        const cardInner = selectedCard.querySelector('.c-block-54');
        if (cardInner) {
            cardInner.innerHTML = '';
            cardInner.classList.remove('is-disabled');
        }
        selectedCard.classList.add('is-hide');

        const errorMsg = this.element.querySelector('[data-js-selected-offer-error]');
        if (errorMsg) errorMsg.classList.add('is-hide');
        this._validate();
    }

    // 保存ボタンが押された時
    handleOnSubmit(e) {
        e.preventDefault();

        if (this._isValid()) {
            const $close = $(
                `.js-modal-basic-close[data-modalid="scout_template_modal"]`
            );
            // initializeTemplateValuesがDOMをクリアする前に、添付求人の非活性状態を捕捉
            const offerWasInactive = !!this.offeridValue && !this._isAttachedOfferActive();
            this._saveTemplate().then(json => {
                if (offerWasInactive && json && json['scout_template_offer']) {
                    // 添付求人が募集中以外（停止/申請中/下書き）の場合は、スカウト本体への適用も行わない
                    json['scout_template_offer'] = null;
                }
                $(this.element).trigger("scout_template_form_v2:submit", {
                    template: json,
                });
            });
            this.initializeTemplateValues();
            $close.trigger("click");
        }
    }

    // テンプレートフォームの値を初期化する
    initializeTemplateValues() {
        this.setTemplateValues("", "store", "", "", "", "", "", "", "");
        this._renderBrandStoreSelectOptions();
    }

    // テンプレートフォームの値を設定する
    setTemplateValues(
        templateId,
        scouttype,
        templateTitle,
        brandid,
        storeid,
        sender,
        title,
        content,
        offerid
    ) {
        this.templateidValue = templateId;
        this.scouttypeValue = scouttype;
        this.templateTitleValue = templateTitle;
        if (this.scouttypeValue === "company") {
            this.brandidValue = "";
            this.storebrandidValue = "";
            this.storeidValue = "";
        } else if (this.scouttypeValue === "brand") {
            this.brandidValue = brandid || '';
            this.storebrandidValue = "";
            this.storeidValue = "";
        } else if (this.scouttypeValue === "store") {
            this.brandidValue = '';
            this.storebrandidValue = brandid || '';
            this.storeidValue = storeid || '';
        }
        this.senderValue = sender;
        this.titleValue = title;
        this.contentValue = content;
        this.offeridValue = offerid || "";
        this._setValueAllTargets();
        this._setSelectedOffer(offerid);
        this._validate();
    }

    // 添付求人カードの表示を切り替える（編集時の初期表示・新規時のクリアで使用）
    _setSelectedOffer(offerid) {
        // 求人タイトルは下の選択済みカードに表示する仕様のため、「選択」ボタンのラベルは変更しない
        if (offerid) {
            // フェッチ完了前に古いカードのデータでエラーが表示されるのを防ぐため先にクリアする
            this._clearOfferCardContent();
            this._fetchSelectedOfferCard(offerid);
        } else {
            this._hideSelectedOfferCard();
        }
    }

    _clearOfferCardContent() {
        const selectedCard = this.element.querySelector('[data-js-selected-offer-card]');
        if (!selectedCard) return;
        const cardInner = selectedCard.querySelector('.c-block-54');
        if (cardInner) {
            // フェッチ完了までの間、_isAttachedOfferActive() が「有効」と誤判定しないようダミーで無効扱いにする
            cardInner.innerHTML = '<div data-offer-active="false" data-offer-brand-id="" data-offer-store-id=""></div>';
            cardInner.classList.add('is-disabled');
        }
        const errorMsg = this.element.querySelector('[data-js-selected-offer-error]');
        if (errorMsg) errorMsg.classList.add('is-hide');
        const scopeError = this.element.querySelector('[data-js-selected-offer-scope-error]');
        if (scopeError) {
            scopeError.classList.add('is-hide');
            scopeError.textContent = '';
        }
    }

    _fetchSelectedOfferCard(offerid) {
        const ENDPOINT_URL = `/manage/templates/scouts/offer_card?offer_id=${encodeURIComponent(offerid)}`;

        return fetch(ENDPOINT_URL, {
            method: "GET",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                "Accept": "text/html",
                "X-CSRF-Token": getCsrfToken(),
            },
        })
            .then(res => {
                if (!res.ok) throw new Error(`Failed to fetch offer card: ${res.status}`);
                return res.text();
            })
            .then(html => {
                if (String(this.offeridValue) === String(offerid)) {
                    this._renderSelectedOfferCard(html);
                }
            })
            .catch(error => {
                console.error(error);
                if (String(this.offeridValue) === String(offerid)) {
                    this._hideSelectedOfferCard();
                }
            });
    }

    // テンプレートフォームを保存する
    _saveTemplate() {
        const ENDPOINT_URL = this.templateidValue
            ? `/manage/scouts/templates/${this.templateidValue}.json`
            : `/manage/scouts/templates.json`;
        const body = {
            scout_template: {
                title: this.titleValue,
                content: this.contentValue,
                template_title: this.templateTitleValue,
                sender: this.senderValue,
                template_type: this.scouttypeValue,
            },
        };

        const brandId = this.scouttypeValue === 'store' ? this.storebrandidValue : this.brandidValue;
        this.scouttypeValue !== 'company' && brandId
            ? (body.scout_template.scout_template_brand_attributes = {
                  brand_id: brandId,
              })
            : undefined;
        this.scouttypeValue !== 'company' && this.storeidValue
            ? (body.scout_template.scout_template_store_attributes = {
                  store_id: this.storeidValue,
              })
            : undefined;
        // 添付求人が募集中（release）以外（停止/申請中/下書き）になっている場合は送信に含めない
        this.offeridValue && this._isAttachedOfferActive()
            ? (body.scout_template.scout_template_offer_attributes = {
                  offer_id: this.offeridValue,
              })
            : undefined;

        const requestBody = {
            method: this.templateidValue ? "PATCH" : "POST",
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCsrfToken(),
            },
            body: JSON.stringify(body),
        };
        return fetch(ENDPOINT_URL, requestBody)
            .then(res => res.json())
            .then(json => {
                // console.log(this.templateidValue, json)
                const scoutTemplateModal = document.querySelector(
                    `div[data-controller="scout-template-modal-v2"]`
                );
                this.application
                    .getControllerForElementAndIdentifier(
                        scoutTemplateModal,
                        "scout-template-modal-v2"
                    )
                    .fetchTemplate();
                const uneditScoutTemplateModal = document.querySelector(
                    `div[data-controller="unedit-scout-template-modal"]`
                );
                if (uneditScoutTemplateModal) {
                    this.application
                        .getControllerForElementAndIdentifier(
                            uneditScoutTemplateModal,
                            "unedit-scout-template-modal"
                        )
                        ?.fetchTemplate();
                }
                return json;
            });
    }

    // フォーム内のinputのvalueを設定する
    _setValueAllTargets() {
        document
            .querySelectorAll(
                `#${this.element.id} input[name="${this.scouttypeTarget.name}"]`
            )
            .forEach(scoutType => {
                if (this.scouttypeValue === scoutType.value) {
                    scoutType.checked = true;
                }
            });
        this.templateTitleTarget.value = this.templateTitleValue;
        if (this.scouttypeValue === "company") {
            this.brandidTarget.value = "";
            this.storebrandidTarget.value = "";
            this.storeidTarget.value = "";
        } else if (this.scouttypeValue === "brand") {
            this.brandidTarget.value = this.brandidValue;
            this.storebrandidTarget.value = "";
            this.storeidTarget.value = "";
            this._renderBrandStoreSelectOptions();
        } else if (this.scouttypeValue === "store") {
            this.brandidTarget.value = "";
            let storeId = this.storeidValue;
            let storebrandId = this.storebrandidValue;
            this._renderBrandStoreSelectOptions(storebrandId, storeId).finally(
                () => {
                    this.storeidTarget.value = storeId;
                    this.storebrandidTarget.value = storebrandId;
                    this._validate();
                }
            );
        }
        this.senderTarget.value = this.senderValue;
        this.titleTarget.value = this.titleValue;
        this.contentTarget.value = this.contentValue;
    }

    // フォームのバリデーション
    _validate() {
        if (this._isValid()) {
            this.submitTarget.disabled = false;
            this.submitTarget.classList.remove("is-disabled");
        } else {
            this.submitTarget.disabled = true;
            this.submitTarget.classList.add("is-disabled");
        }
        this._renderAttachedOfferScopeMismatchError();
    }

    // フォームのバリデーション
    _isValid() {
        const isValidForm = !!(
            this.templateTitleValue &&
            this.titleValue &&
            this.contentValue
        );
        // 求人が添付されている場合は「募集中」であることを必須にする。停止/申請中/下書き/論理削除済みは保存不可。
        const isValidAttachedOffer = !this.offeridValue || this._isAttachedOfferActive();
        // 添付求人のブランド/店舗がスカウト単位と一致しない場合は保存不可
        const isAttachedOfferScopeMatched = this._isAttachedOfferScopeMatched();

        return this._isValidScoutType() && isValidForm && isValidAttachedOffer && isAttachedOfferScopeMatched;
    }

    _isValidScoutType() {
        if (this.scouttypeValue === 'company') return true;
        if (this.scouttypeValue === 'brand') {
            return !!(this.brandidValue && this.brandidTarget.value);
        }
        if (this.scouttypeValue === 'store') {
            return !!(this.storebrandidValue && this.storeidValue && this.storeidTarget.value);
        }
        return false;
    }

    // ブランドと店舗のセレクトボックスのoptionを描画する
    async _renderBrandStoreSelectOptions(brandId = null, defaultValue = null) {
        let html = `<option value="">先にブランドを選択してください</option>`;

        if (!brandId) {
            this.storeidTarget.innerHTML = html;
            this.storeidTarget.classList.add("is-disabled");
            this.storeidTarget.setAttribute("readonly", true);
            this.storeidTarget.setAttribute("disabled", true);
            return;
        }

        const stores = await fetchStores(brandId);
        stores.map(store => {
            html += `<option value="${store.id}" data-id="${store.id}" ${String(defaultValue) === String(store.id) ? 'selected="true"' : ""}>${store.name}</option>`;
        });
        this.storeidTarget.innerHTML = html;
        this.storeidTarget.classList.remove("is-disabled");
        this.storeidTarget.removeAttribute("readonly");
        this.storeidTarget.removeAttribute("disabled");
    }

    // scouttypeによって表示を切り替える
    _displayOnScouttype(selectValue) {
        const $brandElement = document.querySelector(
            `#${this.element.id} div[name="scout_template_unit_brand"]`
        );
        const $storeElement = document.querySelector(
            `#${this.element.id} div[name="scout_template_unit_store"]`
        );

        if (selectValue === "company") {
            $brandElement.classList.add("is-hide");
            $storeElement.classList.add("is-hide");
        } else if (selectValue === "brand") {
            $brandElement.classList.remove("is-hide");
            $storeElement.classList.add("is-hide");
        } else if (selectValue === "store") {
            $brandElement.classList.add("is-hide");
            $storeElement.classList.remove("is-hide");
        }
    }
}
